import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import { useFirebase } from './contexts/FirebaseContext.js';
import Collections from './Collections.js';

// Import custom components
import SearchBar from './components/home/SearchBar.js';
import MobileHeader from './components/home/MobileHeader.js';
import RecipeCategories from './components/home/RecipeCategories.js';

// Import custom hooks
import useSearchSuggestions from './hooks/useSearchSuggestions.js';
import useRecipeFilters from './hooks/useRecipeFilters.js';

/**
 * HomePage component serves as the landing page for the app
 * 
 * @param {Array} recipes - Array of recipe objects
 * @param {Array} inventory - Array of inventory ingredients
 * @param {Set} favorites - Set of favorite recipe IDs
 * @param {Set} bookmarks - Set of bookmarked recipe IDs
 * @param {Function} toggleFavorite - Function to toggle favorite status
 * @param {Function} toggleBookmark - Function to toggle bookmark status
 * @param {Function} onDarkModeToggle - Function to toggle dark/light mode
 * @param {Function} onLogout - Function to handle user logout
 * @param {Function} resetSearchFilters - Function to reset search filters
 * @param {string} unitPreference - Current unit preference ('imperial' or 'metric')
 * @param {Function} onUnitPreferenceToggle - Function to toggle unit preference
 */
const HomePage = React.memo(({ 
  recipes, 
  inventory, 
  favorites, 
  bookmarks,
  toggleFavorite,
  toggleBookmark,
  onDarkModeToggle, 
  onLogout, 
  resetSearchFilters,
  unitPreference,
  onUnitPreferenceToggle
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { user } = useFirebase();
  
  // Reset filters when landing on homepage
  useEffect(() => {
    if (resetSearchFilters) {
      resetSearchFilters();
    }
  }, [resetSearchFilters]);
  
  // Search state
  const [searchInput, setSearchInput] = useState("");
  const [open, setOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);

  // Get filtered recipes and menu options - memoized through the hook
  const { recipeMenus } = useRecipeFilters(recipes, inventory, favorites, bookmarks);
  
  // Get search suggestions - memoized through the hook
  const suggestions = useSearchSuggestions(recipes, inventory, searchInput);

  // Reset isClearing when searchInput is empty
  useEffect(() => {
    if (searchInput === '') {
      setIsClearing(false);
    }
  }, [searchInput]);

  // Menu handlers - memoized with useCallback
  const handleMenuOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleDarkModeToggle = useCallback(() => {
    onDarkModeToggle();
    handleMenuClose();
  }, [onDarkModeToggle, handleMenuClose]);

  const handleUnitPreferenceToggle = useCallback(() => {
    onUnitPreferenceToggle();
    handleMenuClose();
  }, [onUnitPreferenceToggle, handleMenuClose]);

  const handleLogout = useCallback(() => {
    onLogout();
    handleMenuClose();
  }, [onLogout, handleMenuClose]);

  // Search handlers - memoized with useCallback
  const handleOptionSelect = useCallback((event, value, reason) => {
    if (reason === 'selectOption') {
      if (typeof value === 'object' && value.type !== 'no-matches') {
        if (value.type === 'recipe') {
          // For direct cocktail selection, bypass filters and go directly to detail view
          navigate(`/recipe/${value.id}`);
        } else if (value.type === 'ingredient') {
          // For ingredient selection, show recipes using that ingredient with current filters
          navigate('/recipes', { state: { searchTerms: [value.name], filterByIngredient: true } });
        } else if (value.type === 'search') {
          // For full-text search, respect current filters
          navigate('/recipes', { state: { searchTerms: [searchInput] } });
        }
      }
      setIsClearing(true);
      setSearchInput('');
      setOpen(false);
    }
  }, [navigate, searchInput]);

  const handleInputChange = useCallback((event, newValue) => {
    if (!isClearing) {
      setSearchInput(newValue || '');
      setOpen(!!newValue);
    }
  }, [isClearing]);

  const handleSearch = useCallback(() => {
    if (searchInput.trim()) {
      navigate('/recipes', { state: { searchTerms: [searchInput] } });
    }
  }, [navigate, searchInput]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' && searchInput.trim()) {
      event.preventDefault();
      navigate('/recipes', { state: { searchTerms: [searchInput] } });
    }
  }, [navigate, searchInput]);

  // Navigation handler for recipe categories - now applies filters
  const handleNavigate = useCallback((menuItem) => {
    // Clear existing filters before applying new ones
    localStorage.removeItem('recipeSearchTerms');
    
    // Determine which status filter to apply based on the menu item
    let statusFilter = null;
    
    if (menuItem.name === 'Favorites') {
      statusFilter = 'favorites';
    } else if (menuItem.name === 'Bookmarks') {
      statusFilter = 'bookmarks';
    } else if (menuItem.name === 'Recipes I Can Make') {
      statusFilter = 'can-make';
    } else if (menuItem.name === 'Missing 1 Ingredient') {
      statusFilter = 'missing-1';
    } else if (menuItem.name === 'Missing 2 Ingredients') {
      statusFilter = 'missing-2';
    }
    
    // If we have a status filter to apply, save it and navigate to recipes page
    if (statusFilter) {
      localStorage.setItem('recipeStatusFilters', JSON.stringify([statusFilter]));
    } else {
      localStorage.removeItem('recipeStatusFilters');
    }
    
    // Always set a default sort order
    localStorage.setItem('recipeSortOrder', 'asc');
    
    // Navigate to the recipes page
    navigate('/recipes/all');
  }, [navigate]);

  // Memoize the core JSX structure
  const mobileHeader = useMemo(() => {
    if (!isMobile) return null;
    
    return (
      <MobileHeader 
        user={user}
        anchorEl={anchorEl}
        handleMenuOpen={handleMenuOpen}
        handleMenuClose={handleMenuClose}
        handleDarkModeToggle={handleDarkModeToggle}
        handleUnitPreferenceToggle={handleUnitPreferenceToggle}
        handleLogout={handleLogout}
        unitPreference={unitPreference}
      />
    );
  }, [
    isMobile, user, anchorEl, handleMenuOpen, handleMenuClose, 
    handleDarkModeToggle, handleUnitPreferenceToggle, 
    handleLogout, unitPreference
  ]);

  return (
    <Box sx={{ 
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      pb: 8 // Add padding at bottom to account for bottom nav
    }}>
      {/* Mobile-only Header */}
      {mobileHeader}

      <Box sx={{ 
        p: isMobile ? { xs: '0.75rem 1.5rem' } : { sm: 3 },
        pt: isMobile ? 1 : 2
      }}>
        {/* 1. Search Section - Fixed at top */}
        <Container 
          maxWidth="md" 
          disableGutters={isMobile}
          sx={{
            mb: { xs: 3, sm: 4 }
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%'
          }}>
            <SearchBar
              searchInput={searchInput}
              setSearchInput={setSearchInput}
              open={open}
              setOpen={setOpen}
              isClearing={isClearing}
              suggestions={suggestions}
              handleOptionSelect={handleOptionSelect}
              handleInputChange={handleInputChange}
              handleSearch={handleSearch}
              handleKeyDown={handleKeyDown}
            />
          </Box>
        </Container>

        {/* 2. Action Filters - Recipe Category Buttons */}
        <Container 
          maxWidth="md" 
          disableGutters={isMobile}
          sx={{
            mb: { xs: 3, sm: 4 }
          }}
        >
          <RecipeCategories 
            recipeMenus={recipeMenus} 
            onNavigate={handleNavigate} 
          />
        </Container>

        {/* 3. Collections Section */}
        <Container 
          maxWidth="md" 
          disableGutters={isMobile}
        >
          <Collections 
            recipes={recipes}
          />
        </Container>
      </Box>
    </Box>
  );
});

export default HomePage; 